import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { regenerateAfterImage } from "@/lib/regenerate-demo.functions";
import { getImageStats, hamming64, formatBytes, type ImageStats } from "@/components/image-qa";
import { useT } from "@/i18n/locale-context";

type Week = {
  n: number;
  label: string;
  reveal: number; // 0..100 — how much of "after" is revealed at this week
  skin: number;
  posture: number;
  eye: number;
};

const WEEKS: Week[] = [
  { n: 1, label: "S01", reveal: 40, skin: 46, posture: 50, eye: 42 },
  { n: 2, label: "S02", reveal: 55, skin: 58, posture: 57, eye: 50 },
  { n: 3, label: "S03", reveal: 70, skin: 72, posture: 66, eye: 60 },
  { n: 4, label: "S04", reveal: 88, skin: 86, posture: 78, eye: 72 },
];

const demoImages = import.meta.glob("../assets/demo/*.jpg", { eager: true, import: "default" }) as Record<string, string>;

function getDemoImage(fileName: string) {
  const image = demoImages[`../assets/demo/${fileName}`];
  if (!image) {
    throw new Error(`Missing demo image: ${fileName}`);
  }
  return image;
}

type SubjectKey =
  | "matureMan1"
  | "matureMan2"
  | "midageMan1"
  | "midageMan2"
  | "youngMan1"
  | "youngMan2"
  | "matureWoman1"
  | "matureWoman2"
  | "midageWoman1"
  | "midageWoman2"
  | "youngWoman1"
  | "youngWoman2";

const SUBJECT_KEYS: SubjectKey[] = [
  "matureMan1",
  "matureMan2",
  "midageMan1",
  "midageMan2",
  "youngMan1",
  "youngMan2",
  "matureWoman1",
  "matureWoman2",
  "midageWoman1",
  "midageWoman2",
  "youngWoman1",
  "youngWoman2",
];

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const SUBJECTS: Record<SubjectKey, { before: string; afters: [string, string, string, string] }> = {
  matureMan1: {
    before: getDemoImage("mature-man-1-week0-before.jpg"),
    afters: [
      getDemoImage("mature-man-1-week1-after.jpg"),
      getDemoImage("mature-man-1-week2-after.jpg"),
      getDemoImage("mature-man-1-week3-after.jpg"),
      getDemoImage("mature-man-1-week4-after.jpg"),
    ],
  },
  matureMan2: {
    before: getDemoImage("mature-man-2-week0-before.jpg"),
    afters: [
      getDemoImage("mature-man-2-week1-after.jpg"),
      getDemoImage("mature-man-2-week2-after.jpg"),
      getDemoImage("mature-man-2-week3-after.jpg"),
      getDemoImage("mature-man-2-week4-after.jpg"),
    ],
  },
  midageMan1: {
    before: getDemoImage("midage-man-1-week0-before.jpg"),
    afters: [
      getDemoImage("midage-man-1-week1-after.jpg"),
      getDemoImage("midage-man-1-week2-after.jpg"),
      getDemoImage("midage-man-1-week3-after.jpg"),
      getDemoImage("midage-man-1-week4-after.jpg"),
    ],
  },
  midageMan2: {
    before: getDemoImage("midage-man-2-week0-before.jpg"),
    afters: [
      getDemoImage("midage-man-2-week1-after.jpg"),
      getDemoImage("midage-man-2-week2-after.jpg"),
      getDemoImage("midage-man-2-week3-after.jpg"),
      getDemoImage("midage-man-2-week4-after.jpg"),
    ],
  },
  youngMan1: {
    before: getDemoImage("young-man-3-week0-before.jpg"),
    afters: [
      getDemoImage("young-man-3-week1-after.jpg"),
      getDemoImage("young-man-3-week2-after.jpg"),
      getDemoImage("young-man-3-week3-after.jpg"),
      getDemoImage("young-man-3-week4-after.jpg"),
    ],
  },
  youngMan2: {
    before: getDemoImage("young-man-2-week0-before.jpg"),
    afters: [
      getDemoImage("young-man-2-week1-after.jpg"),
      getDemoImage("young-man-2-week2-after.jpg"),
      getDemoImage("young-man-2-week3-after.jpg"),
      getDemoImage("young-man-2-week4-after.jpg"),
    ],
  },
  matureWoman1: {
    before: getDemoImage("mature-woman-1-week0-before.jpg"),
    afters: [
      getDemoImage("mature-woman-1-week1-after.jpg"),
      getDemoImage("mature-woman-1-week2-after.jpg"),
      getDemoImage("mature-woman-1-week3-after.jpg"),
      getDemoImage("mature-woman-1-week4-after.jpg"),
    ],
  },
  matureWoman2: {
    before: getDemoImage("mature-woman-2-week0-before.jpg"),
    afters: [
      getDemoImage("mature-woman-2-week1-after.jpg"),
      getDemoImage("mature-woman-2-week2-after.jpg"),
      getDemoImage("mature-woman-2-week3-after.jpg"),
      getDemoImage("mature-woman-2-week4-after.jpg"),
    ],
  },
  midageWoman1: {
    before: getDemoImage("midage-woman-1-week0-before.jpg"),
    afters: [
      getDemoImage("midage-woman-1-week1-after.jpg"),
      getDemoImage("midage-woman-1-week2-after.jpg"),
      getDemoImage("midage-woman-1-week3-after.jpg"),
      getDemoImage("midage-woman-1-week4-after.jpg"),
    ],
  },
  midageWoman2: {
    before: getDemoImage("woman-week0-before.jpg"),
    afters: [
      getDemoImage("woman-week1-after.jpg"),
      getDemoImage("woman-week2-after.jpg"),
      getDemoImage("woman-week3-after.jpg"),
      getDemoImage("woman-week4-after.jpg"),
    ],
  },
  youngWoman1: {
    before: getDemoImage("young-week0-before.jpg"),
    afters: [
      getDemoImage("young-week1-after.jpg"),
      getDemoImage("young-week2-after.jpg"),
      getDemoImage("young-week3-after.jpg"),
      getDemoImage("young-week4-after.jpg"),
    ],
  },
  youngWoman2: {
    before: getDemoImage("young-woman-2-week0-before.jpg"),
    afters: [
      getDemoImage("young-woman-2-week1-after.jpg"),
      getDemoImage("young-woman-2-week2-after.jpg"),
      getDemoImage("young-woman-2-week3-after.jpg"),
      getDemoImage("young-woman-2-week4-after.jpg"),
    ],
  },
};

const WEEK_PROMPTS: [string, string, string, string] = [
  // Each prompt forces a DISTINCT location, time of day, light temperature, color palette and outfit so the four After frames never look like the same shoot.
  "Same person, strictly identical facial identity (face shape, eyes, nose, mouth, hairline, ears, skin marks, body shape). NEW DAY — LOCATION: home bathroom in front of a mirror, white subway tile wall behind. LIGHTING: cool overhead bathroom light (~5000K), slight top shadow under the eyes. OUTFIT: plain charcoal crew-neck t-shirt. POSE: head turned ~10° to the left, slight bathroom-mirror angle. CHANGES: skin only slightly less tired, faint freshness, neutral expression. Photorealistic smartphone selfie, no makeup, no over-retouching. The background, lighting color and framing MUST clearly differ from any other day.",
  "Same person, strictly identical facial identity and body. ANOTHER DAY — LOCATION: outdoor café terrace, blurred street and a wooden chair in the background. LIGHTING: warm late-afternoon sun coming from the upper right (~3200K), soft warm skin tones, golden rim on the cheek. OUTFIT: light grey zip hoodie with the hood down over a white tee. POSE: head straight, chin slightly lowered, shoulders relaxed. CHANGES: clearer hydrated skin, reduced dark circles, calm relaxed expression. Photorealistic smartphone selfie. The background, color temperature and outfit MUST clearly differ from every other week.",
  "Same person, strictly identical facial identity and body. DIFFERENT DAY — LOCATION: indoor office or co-working space, large window on the LEFT showing a blurred grey city skyline, plain off-white wall on the right. LIGHTING: bright cool diffused daylight from the left side (~6000K), gentle side shadow on the right cheek. OUTFIT: navy-blue button-up shirt open at the collar. POSE: head slightly tilted ~8° to the right, confident upright posture. CHANGES: visibly healthier glowing even skin, bright awake eyes, subtle soft smile. Photorealistic smartphone selfie. The room, light direction and color palette MUST clearly differ from every other week.",
  "Same person, strictly identical facial identity and body. LATER DAY — LOCATION: outdoor park or garden path, blurred deep green foliage and a hint of a tree trunk behind. LIGHTING: warm golden-hour sun from low and behind, soft halo on the hair, no harsh shadows on the face. OUTFIT: beige linen shirt with the top two buttons open. POSE: head straight, shoulders squared, looking confidently into the camera. CHANGES: radiant clear skin with natural glow, bright rested eyes, genuine relaxed smile, fresh confident appearance. Photorealistic smartphone selfie, believable, not over-retouched. The outdoor green setting and warm golden light MUST clearly differ from every other week — no white walls, no bathroom, no office.",
];

type RegenMeta = { model: string; elapsedMs: number; approxBytes: number; generatedAt: number };
type StatsByWeek = (ImageStats | null)[];

async function urlToDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function BeforeAfterDemo() {
  const t = useT();
  const regenerate = useServerFn(regenerateAfterImage);
  const SUBJECT_ORDER = useMemo<SubjectKey[]>(() => shuffle(SUBJECT_KEYS), []);
  const [subjectIdx, setSubjectIdx] = useState(0);
  const [rotating, setRotating] = useState(true);
  const [weekIdx, setWeekIdx] = useState(3);
  const [reveal, setReveal] = useState(WEEKS[3].reveal);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [overrides, setOverrides] = useState<Partial<Record<SubjectKey, (string | null)[]>>>({});
  const [regenLoading, setRegenLoading] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [regenMeta, setRegenMeta] = useState<Partial<Record<SubjectKey, (RegenMeta | null)[]>>>({});
  const [qaBefore, setQaBefore] = useState<ImageStats | null>(null);
  const [qaAfters, setQaAfters] = useState<StatsByWeek>([null, null, null, null]);
  const [qaLoading, setQaLoading] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);

  const week = WEEKS[weekIdx];
  const subject = SUBJECT_ORDER[subjectIdx];
  const subjectImgs = SUBJECTS[subject];
  const afterSrc = overrides[subject]?.[weekIdx] ?? subjectImgs.afters[weekIdx];

  async function handleRegenerate() {
    if (regenLoading) return;
    setRegenLoading(true);
    setRotating(false);
    setPlaying(false);
    const currentSubject = subject;
    try {
      const beforeDataUrl = await urlToDataUrl(subjectImgs.before);
      // Reset overrides for this subject and stream in as results arrive.
      setOverrides((prev) => ({ ...prev, [currentSubject]: [null, null, null, null] }));
      setRegenMeta((prev) => ({ ...prev, [currentSubject]: [null, null, null, null] }));
      await Promise.all(
        WEEK_PROMPTS.map(async (prompt, i) => {
          try {
            const { dataUrl, meta } = await regenerate({ data: { beforeImageDataUrl: beforeDataUrl, prompt } });
            setOverrides((prev) => {
              const arr = [...(prev[currentSubject] ?? [null, null, null, null])];
              arr[i] = dataUrl;
              return { ...prev, [currentSubject]: arr };
            });
            setRegenMeta((prev) => {
              const arr = [...(prev[currentSubject] ?? [null, null, null, null])];
              arr[i] = meta as RegenMeta;
              return { ...prev, [currentSubject]: arr };
            });
          } catch (err) {
            console.error(`Week ${i + 1} regen failed`, err);
          }
        }),
      );
      toast.success(t("index.demo.regen.done"));
    } catch (err) {
      console.error(err);
      toast.error(t("index.demo.regen.error"));
    } finally {
      setRegenLoading(false);
    }
  }

  // Sync slider to week when week changes (unless user is dragging)
  useEffect(() => {
    if (!dragging) setReveal(week.reveal);
  }, [weekIdx, week.reveal, dragging]);

  // Auto-play through weeks
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      setWeekIdx((i) => (i + 1) % WEEKS.length);
    }, 1800);
    return () => window.clearInterval(id);
  }, [playing]);

  // Auto-rotate through subjects
  useEffect(() => {
    if (!rotating) return;
    const id = window.setInterval(() => {
      setSubjectIdx((i) => (i + 1) % SUBJECT_ORDER.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [rotating, SUBJECT_ORDER.length]);

  // Close zoom on Escape
  useEffect(() => {
    if (!zoomed) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomed(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [zoomed]);

  function goToSubject(next: number) {
    const len = SUBJECT_ORDER.length;
    setSubjectIdx(((next % len) + len) % len);
    setRotating(false);
  }

  function setRevealFromClientX(clientX: number) {
    const el = frameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setReveal(Math.max(0, Math.min(100, pct)));
  }

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragging(true);
    setPlaying(false);
    setRevealFromClientX(e.clientX);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return;
    setRevealFromClientX(e.clientX);
  }
  function onPointerUp() {
    setDragging(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowLeft") setReveal((r) => Math.max(0, r - 4));
    if (e.key === "ArrowRight") setReveal((r) => Math.min(100, r + 4));
  }

  // Recompute QA stats when QA is open and the active subject/overrides change.
  useEffect(() => {
    if (!qaOpen) return;
    let cancelled = false;
    setQaLoading(true);
    setQaBefore(null);
    setQaAfters([null, null, null, null]);
    (async () => {
      try {
        const beforeStats = await getImageStats(subjectImgs.before);
        if (cancelled) return;
        setQaBefore(beforeStats);
        const afterSrcs = WEEKS.map((_, i) => overrides[subject]?.[i] ?? subjectImgs.afters[i]);
        const results = await Promise.all(
          afterSrcs.map(async (src) => {
            try {
              return await getImageStats(src);
            } catch {
              return null;
            }
          }),
        );
        if (!cancelled) setQaAfters(results);
      } catch (err) {
        console.error("QA stats failed", err);
      } finally {
        if (!cancelled) setQaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qaOpen, subject, subjectImgs, overrides]);

  return (
    <div>
      {/* Comparison slider */}
      <div
        ref={frameRef}
        className="relative w-full aspect-[4/5] sm:aspect-[16/10] rounded-2xl overflow-hidden ring-1 ring-foreground/5 select-none touch-none cursor-ew-resize group"
        onMouseEnter={() => setRotating(false)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Before (full) — slightly desaturated and cooler so the "past" reads instantly */}
        <img
          src={subjectImgs.before}
          alt={t("index.demo.before")}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ filter: "grayscale(0.5) brightness(0.94) contrast(0.96)" }}
          draggable={false}
        />
        {/* After clipped to reveal (clip-path keeps the image undistorted) */}
        <img
          src={afterSrc}
          alt={t("index.demo.after")}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ clipPath: `inset(0 ${100 - reveal}% 0 0)` }}
          draggable={false}
        />

        {/* Top split band — unambiguous BEFORE | AFTER aligned with the slider */}
        <div className="absolute top-0 inset-x-0 h-7 pointer-events-none flex">
          <div
            className="h-full bg-accent text-accent-foreground flex items-center justify-start pl-3 overflow-hidden transition-[width] duration-75"
            style={{ width: `${reveal}%` }}
          >
            <span className="text-[10px] font-bold tracking-[0.18em] uppercase whitespace-nowrap">
              {t("index.demo.after")}
            </span>
          </div>
          <div
            className="h-full bg-foreground/85 text-background flex items-center justify-end pr-3 overflow-hidden transition-[width] duration-75"
            style={{ width: `${100 - reveal}%` }}
          >
            <span className="text-[10px] font-bold tracking-[0.18em] uppercase whitespace-nowrap">
              {t("index.demo.before")}
            </span>
          </div>
        </div>

        {/* Expand to fullscreen — top-left, clear of slider line */}
        <button
          type="button"
          aria-label={t("index.demo.expand")}
          title={t("index.demo.expand")}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setZoomed(true);
            setRotating(false);
            setPlaying(false);
          }}
          className="absolute top-10 left-3 size-9 rounded-full bg-background/90 backdrop-blur grid place-items-center shadow-elegant ring-1 ring-foreground/10 hover:bg-background z-10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>

        {/* Drag handle */}
        <div
          role="slider"
          aria-label={t("index.demo.slider.hint")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(reveal)}
          tabIndex={0}
          onKeyDown={onKey}
          className="absolute top-0 bottom-0 -translate-x-1/2 flex items-center justify-center"
          style={{ left: `${reveal}%` }}
        >
          <div className="absolute top-0 bottom-0 w-0.5 bg-background/95 shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" />
          <div className="relative size-10 rounded-full bg-background text-foreground grid place-items-center shadow-elegant ring-1 ring-foreground/10">
            <span className="text-[14px] font-bold leading-none">‹›</span>
          </div>
        </div>

        {/* Hint */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-foreground/80 text-background text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full">
          {t("index.demo.slider.hint")}
        </div>

        {/* Carousel prev/next */}
        <button
          type="button"
          aria-label={t("index.demo.timeline.prev")}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            goToSubject(subjectIdx - 1);
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 size-9 rounded-full bg-background/85 backdrop-blur grid place-items-center shadow-elegant ring-1 ring-foreground/10 hover:bg-background transition-opacity opacity-80 hover:opacity-100"
        >
          <span className="text-[16px] font-bold leading-none">‹</span>
        </button>
        <button
          type="button"
          aria-label={t("index.demo.timeline.next")}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            goToSubject(subjectIdx + 1);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 size-9 rounded-full bg-background/85 backdrop-blur grid place-items-center shadow-elegant ring-1 ring-foreground/10 hover:bg-background transition-opacity opacity-80 hover:opacity-100"
        >
          <span className="text-[16px] font-bold leading-none">›</span>
        </button>

        {/* Carousel dots */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
          {SUBJECT_ORDER.map((s, i) => (
            <button
              key={s}
              type="button"
              aria-label={t(`index.demo.subject.${s}`)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                goToSubject(i);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === subjectIdx ? "w-5 bg-background" : "w-1.5 bg-background/50 hover:bg-background/80"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Week timeline */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {t("index.demo.timeline.kicker")}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenLoading}
              className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-foreground/15 hover:bg-foreground/5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {regenLoading ? t("index.demo.regen.loading") : t("index.demo.regen.cta")}
            </button>
            <button
              type="button"
              onClick={() => setQaOpen((q) => !q)}
              aria-pressed={qaOpen}
              className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border transition-colors ${
                qaOpen
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-foreground/15 hover:bg-foreground/5"
              }`}
            >
              {t("index.demo.qa.cta")}
            </button>
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-foreground/15 hover:bg-foreground/5"
            >
              {playing ? t("index.demo.week.pause") : t("index.demo.week.play")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {WEEKS.map((w, i) => {
            const active = i === weekIdx;
            return (
              <button
                key={w.n}
                type="button"
                onClick={() => {
                  setPlaying(false);
                  setWeekIdx(i);
                }}
                className={`text-left p-3 rounded-xl border transition-all ${
                  active
                    ? "border-accent bg-accent/5 ring-1 ring-accent"
                    : "border-foreground/10 hover:border-foreground/30"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold tracking-tighter">{w.label}</span>
                  <span className={`text-[10px] font-bold ${active ? "text-accent" : "text-muted-foreground"}`}>
                    {w.skin}/100
                  </span>
                </div>
                <div className="h-1 w-full bg-foreground/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${active ? "bg-accent" : "bg-foreground/40"}`}
                    style={{ width: `${w.skin}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {qaOpen && (
        <div className="mt-6 rounded-xl border border-foreground/10 bg-foreground/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              {t("index.demo.qa.kicker")}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {qaLoading ? t("index.demo.qa.loading") : t("index.demo.qa.hint")}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mb-3">
            <span className="font-bold text-foreground">{t("index.demo.before")}:</span>{" "}
            {qaBefore
              ? `${qaBefore.width}×${qaBefore.height} · ${formatBytes(qaBefore.bytes)}`
              : "…"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {WEEKS.map((w, i) => {
              const stats = qaAfters[i];
              const meta = regenMeta[subject]?.[i] ?? null;
              const overridden = !!overrides[subject]?.[i];
              const distance =
                qaBefore && stats ? hamming64(qaBefore.hash, stats.hash) : null;
              const distancePct = distance != null ? Math.round((distance / 64) * 100) : null;
              return (
                <div
                  key={w.n}
                  className="rounded-lg border border-foreground/10 bg-background p-3 text-[11px] leading-relaxed"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold tracking-tighter">{w.label}</span>
                    <span
                      className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        overridden
                          ? "bg-accent/15 text-accent"
                          : "bg-foreground/10 text-muted-foreground"
                      }`}
                    >
                      {overridden ? t("index.demo.qa.source.ai") : t("index.demo.qa.source.asset")}
                    </span>
                  </div>
                  <div className="space-y-0.5 text-muted-foreground">
                    <div>
                      <span className="text-foreground font-medium">{t("index.demo.qa.distance")}: </span>
                      {distance != null ? `${distance}/64 (${distancePct}%)` : "…"}
                    </div>
                    <div>
                      <span className="text-foreground font-medium">{t("index.demo.qa.dimensions")}: </span>
                      {stats ? `${stats.width}×${stats.height}` : "…"}
                    </div>
                    <div>
                      <span className="text-foreground font-medium">{t("index.demo.qa.size")}: </span>
                      {stats ? formatBytes(stats.bytes) : "…"}
                    </div>
                    {meta && (
                      <>
                        <div>
                          <span className="text-foreground font-medium">{t("index.demo.qa.model")}: </span>
                          <span className="break-all">{meta.model}</span>
                        </div>
                        <div>
                          <span className="text-foreground font-medium">{t("index.demo.qa.elapsed")}: </span>
                          {(meta.elapsedMs / 1000).toFixed(2)}s
                        </div>
                        <div>
                          <span className="text-foreground font-medium">{t("index.demo.qa.generatedAt")}: </span>
                          {new Date(meta.generatedAt).toLocaleTimeString()}
                        </div>
                      </>
                    )}
                    <details className="mt-1">
                      <summary className="cursor-pointer text-foreground/70 hover:text-foreground">
                        {t("index.demo.qa.prompt")}
                      </summary>
                      <p className="mt-1 text-[10px] whitespace-pre-wrap">{WEEK_PROMPTS[i]}</p>
                    </details>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {zoomed && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setZoomed(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            aria-label={t("index.demo.close")}
            onClick={() => setZoomed(false)}
            className="absolute top-4 right-4 size-10 rounded-full bg-background/90 text-foreground grid place-items-center shadow-elegant hover:bg-background"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div
            className="relative w-full max-w-3xl aspect-[3/4] sm:aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={subjectImgs.before} alt={t("index.demo.before")} draggable={false} className="absolute inset-0 size-full object-cover" />
            <img
              src={afterSrc}
              alt={t("index.demo.after")}
              draggable={false}
              className="absolute inset-0 size-full object-cover"
              style={{ clipPath: `inset(0 ${100 - reveal}% 0 0)` }}
            />
            <div className="absolute top-3 left-3 bg-background/85 backdrop-blur px-2.5 py-1 rounded-md text-[10px] font-bold tracking-tighter">{t("index.demo.before")}</div>
            <div className="absolute top-3 right-3 bg-accent/90 backdrop-blur px-2.5 py-1 rounded-md text-[10px] font-bold tracking-tighter text-accent-foreground">{t("index.demo.after")}</div>
            <input
              type="range"
              min={0}
              max={100}
              value={reveal}
              onChange={(e) => setReveal(Number(e.target.value))}
              aria-label={t("index.demo.slider")}
              className="absolute inset-x-6 bottom-6 w-[calc(100%-3rem)] accent-accent"
            />
          </div>
        </div>
      )}
    </div>
  );
}
